"use client";

import type { ReactNode } from "react";
import { motion } from "motion/react";

import { UnicodeSpinner } from "@/components/shared/unicode-spinner";
import { Badge } from "@/components/ui/badge";

/**
 * One line of a run's trace as the transcript prints it: the step's badge,
 * its description, and when it happened. TaskEventBlocks derives each line
 * from the run's events; the line itself holds no state.
 */
export function TaskEventTranscriptRow(props: {
  /** The step's badge, e.g. a PluginBadge for a tool call. */
  title: ReactNode;
  description?: ReactNode;
  /** The full line, shown on hover because the row truncates. */
  hint: string;
  dateLabel: string;
  /** The step still running: a spinner leads the line. */
  working?: boolean;
  /** Detail beneath the line, such as matched sessions. */
  children?: ReactNode;
}) {
  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, transform: "translateY(6px)" }}
      animate={{ opacity: 1, transform: "translateY(0px)" }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
      className="border-l border-border/70 pl-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          {props.working ? (
            <UnicodeSpinner
              name="braille"
              className="size-4 shrink-0 opacity-60 leading-none"
            />
          ) : null}
          <div
            className="min-w-0 flex-1 truncate whitespace-nowrap text-xs"
            title={props.hint}
          >
            {props.title}
            {props.description ? (
              <span className="text-muted-foreground">
                {` · `}
                {props.description}
              </span>
            ) : null}
          </div>
        </div>
        <Badge variant="datetime" className="shrink-0">
          {props.dateLabel}
        </Badge>
      </div>
      {props.children}
    </motion.div>
  );
}
